import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { LandingPageSummary, SiteSummary } from '../../models/cms-models';
import { LandingPagesService } from '../../services/landing-pages-service';
import { SiteContextService } from '../../services/site-context.service';

@Component({
  selector: 'app-dashboard-page',
  imports: [CommonModule, RouterLink],
  templateUrl: './dashboard-page.html',
  styleUrl: './dashboard-page.css'
})
export class DashboardPage implements OnInit {
  private siteContext = inject(SiteContextService);
  private landingPagesService = inject(LandingPagesService);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  activeSite: SiteSummary | null = null;
  landingPages: LandingPageSummary[] = [];
  loading = false;
  error: string | null = null;
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.siteContext.sites$, this.siteContext.activeSite$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sites, activeSite]) => {
        void this.syncDashboardState(sites, activeSite);
      });
  }

  get activeSiteName(): string {
    return this.activeSite?.name ?? 'Selected site';
  }

  get draftCount(): number {
    return this.landingPages.filter((landingPage) => landingPage.publicationState === 'Draft').length;
  }

  get activeSiteLandingPageCount(): number {
    return this.activeSite?.landingPageCount ?? 0;
  }

  get activeSiteApiKeyCount(): number {
    return this.activeSite?.publicApiKeyCount ?? 0;
  }

  get activeSiteLinkCount(): number {
    return this.activeSite?.linkCount ?? 0;
  }

  get scheduledCount(): number {
    return this.landingPages.filter((landingPage) => landingPage.publicationState === 'Scheduled').length;
  }

  get publishedCount(): number {
    return Math.max(0, this.activeSiteLandingPageCount - this.draftCount - this.scheduledCount);
  }

  private async syncDashboardState(sites: SiteSummary[], activeSite: SiteSummary | null): Promise<void> {
    this.sites = sites;
    this.activeSite = activeSite;

    if (!activeSite) {
      this.landingPages = [];
      this.loadedSiteId = null;
      this.error = null;
      return;
    }

    if (this.loadedSiteId === activeSite.id) {
      return;
    }

    await this.loadLandingPages(activeSite.id);
  }

  private async loadLandingPages(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      this.landingPages = await this.landingPagesService.getSiteLandingPages(siteId);
      this.loadedSiteId = siteId;
    } catch {
      this.landingPages = [];
      this.error = 'Failed to load landing page status for the selected site.';
    } finally {
      this.loading = false;
    }
  }
}
